import * as React from 'react';

import { parseAirbnbId } from './helpers';

import { IProperty } from 'data/entities/property';
import { connect } from 'react-redux';
import { IThunkDispatch } from 'store/middleware';
import api from '../../../api';
import { getImagesByPropertyId } from '../../../entities/images/actions';

import { Button } from '@travelnest/merlin.button';
import { FormInput, FormLabel, FormLabelHelp, FormRow } from '../form';
import { Heading } from '../heading/Heading';
import { Loader } from '../loader/Loader';
import { Panel } from '../panel/Panel';
import { Paragraph } from '../paragraph/Paragraph';

interface IAirbnbImportProps {
	property: IProperty;
	buttonContext?: 'merlin' | 'portal';
	refreshPhotos: (propertyId: number) => Promise<any>;
}

interface IScrapeResult {
	success: boolean;
	explain: string;
}

export const AirbnbPhotoImporterComponent: React.FC<IAirbnbImportProps> = (props) => {
	const { property, refreshPhotos } = props;

	const [airbnbUrlInput, setAirbnbUrl] = React.useState<string>('');
	const [loading, setLoading] = React.useState<boolean>(false);
	const [scrapeResult, setScrapeResult] = React.useState<IScrapeResult>();

	const scrapeAirbnbPhotos = async (airbnbUrl: string, propertyId: number) => {
		setLoading(true);
		setScrapeResult(undefined);

		const airbnbId = parseAirbnbId(airbnbUrl);
		if (!airbnbId) {
			scrapeFail('We couldn’t find any photos from the Airbnb link you provided.');
			return;
		}

		try {
			await api.importer.importAirbnbPhotos(airbnbId, propertyId);
			await refreshPhotos(propertyId);

			scrapeSucceed('Success! Your Airbnb photos can now be seen below.');
		} catch (e) {
			scrapeFail('We couldn’t import your photos from Airbnb.');
		}
	};

	const scrapeFail = (msg: string) => {
		setScrapeResult({ success: false, explain: msg });
		setLoading(false);
	};

	const scrapeSucceed = (msg: string) => {
		setScrapeResult({ success: true, explain: msg });
		setAirbnbUrl('');
		setLoading(false);
	};

	const uploadButtonClick = (e: any) => {
		e.preventDefault();
		scrapeAirbnbPhotos(airbnbUrlInput, property.id);
	};

	const urlInputChange = (e: any) => setAirbnbUrl(e.target.value);

	return (
		<div>
			<Paragraph fillParent="mid">
				If you already list this property on Airbnb, we can import your photos directly! Paste the link to your property below and click ‘Import photos’
			</Paragraph>
			<Paragraph fillParent="mid">
				Use the link guests see when viewing your property on Airbnb - it will look like this: https://www.airbnb.co.uk/rooms/123456
			</Paragraph>
			{!loading &&
				<div>
					<FormRow>
						<FormLabel htmlFor="airbnb_photo_import_url">Airbnb Link</FormLabel>
						<FormLabelHelp>Copy the link for your Airbnb listing.</FormLabelHelp>
						<FormInput id="airbnb_photo_import_url" onChange={urlInputChange} value={airbnbUrlInput} />
					</FormRow>
					<Button id="import-photos-button" onClick={uploadButtonClick}>Import photos</Button>
				</div>
			}
			{loading && !scrapeResult &&
				<div className="airbnb-photo-import__loading-container">
					<Loader loading={true} />
					<div className="airbnb-photo-import__loading-msg-container">
						<Heading type="D1">We’re importing your photos. They will appear below when this is complete.</Heading>
					</div>
				</div>
			}
			{scrapeResult &&
				<div>
					<Panel
						type={scrapeResult.success ? 'success' : 'error'}
						withIcon>
						{scrapeResult.explain}
					</Panel>
				</div>
			}
		</div>
	);
};

const mapDispatchToProps = (dispatch: IThunkDispatch) => ({
	refreshPhotos: (propertyId: number) => dispatch(getImagesByPropertyId(propertyId))
});

export const AirbnbPhotoImporter = connect(null, mapDispatchToProps)(AirbnbPhotoImporterComponent);
